import React, { useEffect, useRef, useState } from 'react';
import notificationsScreen from "assets/images/notifications-screen.png";
import notificationsScreenMobile from "assets/images/notifications-screen-mobile.png";
import detailedUpdatesScreen from "assets/images/detailed-updates-screen.png";
import detailedUpdatesScreenMobile from "assets/images/detailed-updates-screen-mobile.png";
import efficiencyScreen from "assets/images/efficiency-screen.png";
import efficiencyScreenMobile from "assets/images/efficiency-screen-mobile.png";
import './Features.scss';

interface FeatureSectionProps {
  title: string;
  description: string;
  screenImage: string;
  screenImageMobile: string;
  layout?: 'right' | 'left';
  children?: React.ReactNode;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({ 
  title, 
  description, 
  screenImage,
  screenImageMobile,
  layout = 'right',
  children 
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div className={`feature feature-${layout}`} ref={sectionRef}>
      <div className={`feature-text ${isVisible ? 'visible' : ''}`}>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="feature-visual">
        <img 
          src={screenImage}
          alt={title}
          className='feature-visual-desktop'
        />
        <img 
          src={screenImageMobile}
          alt={title} 
          className='feature-visual-mobile'
        />
        {children}
      </div>
    </div>
  );
};

const Features: React.FC = () => {
  return (
    <section className="features-section">
      <FeatureSection
        title="Prompt Notifications"
        description="Receive quick alerts when new documents are filed, court rulings are issued, or case schedules are updated."
        screenImage={notificationsScreen}
        screenImageMobile={notificationsScreenMobile}
        layout="right"
      />
      <FeatureSection
        title="Detailed Updates"
        description="Know exactly what's new, whether it's a court ruling, opposing counsel's filing, or a change to a court event."
        screenImage={detailedUpdatesScreen}
        screenImageMobile={detailedUpdatesScreenMobile}
        layout="left"
      />
      <FeatureSection
        title="Enhanced Efficiency"
        description="Eliminate the need to manually check for updates or rely on once a day notifications that lack detail."
        screenImage={efficiencyScreen}
        screenImageMobile={efficiencyScreenMobile}
        layout="right"
      />
    </section>
  );
};

export default Features;

